import type { Query, QueryKey } from '@tanstack/react-query'
import type { BatchQueue } from './batch-queue'

/**
 * The shape react-query accepts for `refetchInterval`: a fixed number, `false`
 * to disable polling, a function computing the next delay, or `undefined`.
 * Generic over the query's data type so the function form stays assignable from
 * a caller's concretely-typed `refetchInterval` (the `Query` param is
 * contravariant, so `unknown` would not accept it).
 */
type RefetchInterval<TQueryFnData> =
  | number
  | false
  | ((
      query: Query<TQueryFnData, Error, TQueryFnData, QueryKey>,
    ) => number | false | undefined)
  | undefined

/**
 * A `refetchInterval` for queries batched under a `SormBatchProvider`.
 *
 * Only the common numeric form is rewritten: with a `queue` present, a fixed
 * `interval` becomes a function firing on a shared wall-clock cadence — every
 * `interval` ms since the epoch — rather than relative to each query's own last
 * fetch. Queries that settled at different times (e.g. an available-fields query
 * gated on a schema load before it can enqueue) still refetch on the SAME tick,
 * so their batched refetches keep coalescing into one request instead of
 * drifting into one request per query.
 *
 * Everything else passes through unchanged: `false`/`undefined`/caller-supplied
 * function forms, and any interval when un-batched (no `queue`) — so standalone
 * polling behaviour and tests are unaffected.
 */
export function batchedRefetchInterval(
  queue: BatchQueue | null,
  interval: number,
): number | (() => number)
export function batchedRefetchInterval<TQueryFnData>(
  queue: BatchQueue | null,
  interval: RefetchInterval<TQueryFnData>,
): RefetchInterval<TQueryFnData>
export function batchedRefetchInterval<TQueryFnData>(
  queue: BatchQueue | null,
  interval: RefetchInterval<TQueryFnData>,
): RefetchInterval<TQueryFnData> | (() => number) {
  if (queue === null || typeof interval !== 'number') {
    return interval
  }
  return (): number => msUntilNextTick(interval)
}

/**
 * {@link batchedRefetchInterval} for an interval picked per query, e.g. from its
 * data. Pick multiples of one base interval so every tick lands on a shared one.
 */
export function batchedRefetchIntervalBy<TQuery>(
  queue: BatchQueue | null,
  pickInterval: (query: TQuery) => number,
): (query: TQuery) => number {
  if (queue === null) {
    return pickInterval
  }
  return (query) => msUntilNextTick(pickInterval(query))
}

function msUntilNextTick(interval: number): number {
  const now = Date.now()
  const nextTick = (Math.floor(now / interval) + 1) * interval
  return nextTick - now
}
